import { useCallback, useMemo } from "react";
import {
  Anchor,
  Button,
  Flex,
  Group,
  NumberFormatter,
  Text,
  Tooltip,
} from "@mantine/core";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { useLanguage } from "../../../../stores/LanguageStore";
import { getTypeIcon } from "../../workflows/CreateCampaign/flow/flowSettings";
import MainTable from "../../../../layouts/Table/MainTable";
import { IconChevronRight } from "@tabler/icons-react";
import numberColumn from "../../../../layouts/Table/columns/numberColumn";
import { useReportingStore } from "../../../../stores/ReportingStore";
import LoaderStats from "../LoaderStats";
import { useNavigate } from "react-router-dom";

function handleConclusion(type, rate) {
  let lang = useLanguage.getState().language;
  let good = {
    color: "tertiary.8",
    text: lang.workflows.types.good_text,
  };
  let medium = {
    color: "orange",
    text: lang.workflows.types.mid_text,
  };
  let bad = {
    color: "red",
    text: lang.workflows.types.bad_text,
  };
  let neutral = {
    color: "gray",
    text: lang.workflows.types.good_text,
  };

  if (type === "opener") {
    // SMTP & LinkedIn
    if (rate >= 18) {
      return good;
    } else if (rate >= 10) {
      return medium;
    }
    return bad;
  }

  if (type === "clicker") {
    if (rate >= 2) {
      return good;
    } else if (rate >= 1) {
      return medium;
    }
    return bad;
  }

  if (type === "answerer") {
    if (rate >= 5) {
      return good;
    } else if (rate >= 2) {
      return medium;
    }
    return bad;
  }

  if (type === "answer_positive") {
    if (rate >= 2) {
      return good;
    } else if (rate >= 1) {
      return medium;
    }
    return bad;
  }

  if (type === "answer_negative") {
    if (rate <= 50) {
      return good;
    } else if (rate <= 70) {
      return medium;
    }
    return bad;
  }

  if (type === "answer_neutral") {
    return neutral;
  }
}

function handleClick({ field, nav, integrationId, isQuality }) {
  let filter = {
    mode: "and",
    values: [],
  };

  if (!isQuality) {
    filter.values.push({
      field_name: "status",
      type: "equals",
      value: field,
    });
  } else {
    filter.values.push({
      field_name: "is_positive",
      type: "equals",
      value:
        field === "answer_positive"
          ? "1"
          : field === "answer_negative"
          ? "0"
          : "null",
    });
  }

  filter.values.push({
    field_name: "integration_id",
    type: "equals",
    value: integrationId.toString(),
  });

  return nav(`/leads-tracking?filter=${JSON.stringify(filter)}`);
}

const ReportingSendersAll = () => {
  const lang = useLanguage((s) => s.language);
  const apiRef = useGridApiRef();
  const globalStats = useReportingStore((s) => s.globalStats);

  const rows = useMemo(() => {
    return globalStats.integrations_deliverability;
  }, [globalStats.integrations_deliverability]);

  const navigate = useNavigate();
  const getField = useCallback(
    (fieldname, headerName) => {
      return {
        field: fieldname,
        headerName: headerName,
        width: 150,
        renderCell: ({ row, value }) => {
          let rate;
          if (fieldname === "opener") {
            if (row.sent) {
              rate = (value / row.sent) * 100;
              rate = Math.round(rate * 10) / 10;
            }
          } else {
            if (row.opener) {
              rate = (value / row.opener) * 100;
              rate = Math.round(rate * 10) / 10;
            }
          }

          return (
            <Tooltip label={lang.reports.view_leads}>
              <Button
                w="100%"
                h={42}
                gap={"xs"}
                wrap="nowrap"
                className="statistics-button"
                justify="space-between"
                variant="light"
                color={handleConclusion(fieldname, rate).color}
                pl={4}
                pr={4}
                ml={0}
                mr={10}
                rightSection={<IconChevronRight size={18} opacity={0.5} />}
                onClick={() =>
                  handleClick({
                    field: fieldname,
                    integrationId: row.id,
                    nav: navigate,
                  })
                }
              >
                <Text
                  fw={500}
                  ml={8}
                  c={handleConclusion(fieldname, rate).color}
                >
                  {`${
                    rate?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, " ") ||
                    "0"
                  }%`}
                </Text>
              </Button>
            </Tooltip>
          );
        },
      };
    },
    [navigate]
  );

  const getFieldQuality = useCallback(
    (fieldname, headerName) => {
      return {
        field: fieldname,
        headerName: headerName,
        width: 150,
        renderCell: ({ row, value }) => {
          let rate;
          let total =
            row.answer_positive + row.answer_negative + row.answer_neutral;

          let divider = row.answerer > total ? row.answerer : total;
          if (divider) {
            rate = (value / divider) * 100;
            rate = Math.round(rate * 10) / 10;
          }

          return (
            <Tooltip label={lang.reports.view_leads}>
              <Button
                w="100%"
                h={42}
                gap={"xs"}
                wrap="nowrap"
                className="statistics-button"
                justify="space-between"
                variant="light"
                color={handleConclusion(fieldname, rate).color}
                pl={4}
                pr={4}
                ml={0}
                mr={10}
                rightSection={<IconChevronRight size={18} opacity={0.5} />}
                onClick={() =>
                  handleClick({
                    field: fieldname,
                    integrationId: row.id,
                    nav: navigate,
                    isQuality: true,
                  })
                }
              >
                <Text
                  fw={500}
                  ml={8}
                  c={handleConclusion(fieldname, rate).color}
                >
                  {`${
                    rate?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, " ") ||
                    "0"
                  }%`}
                </Text>
              </Button>
            </Tooltip>
          );
        },
      };
    },
    [navigate]
  );

  const columns = useMemo(
    () => [
      {
        field: "id",
        headerName: "ID",
        width: 75,
        type: "number",
        align: "left",
        headerAlign: "left",
        renderCell: ({ value }) => (
          <Group pl={8}>
            <NumberFormatter
              value={value}
              thousandSeparator=" "
              style={{ fontSize: 13 }}
            />
          </Group>
        ),
      },
      {
        field: "type",
        headerName: lang.statistics_integrations.columns.sender,
        width: 200,
        renderCell: ({ value, row }) => {
          let parentLink = "";
          if (value === "Email") {
            parentLink = "email";
          } else if (value.includes("Linkedin")) {
            parentLink = "linkedin";
          }
          return (
            <Group w={"100%"} wrap="nowrap">
              {getTypeIcon(row?.account_type_email || value, false, 22, {
                size: "sm",
                variant: "subtle",
              })}
              <Anchor
                size="md"
                truncate="end"
                href={`/senders/${parentLink}/${row.id}`}
                target="_blank"
              >
                {row?.name || "-"}
              </Anchor>
            </Group>
          );
        },
      },
      numberColumn("sent", lang.reports.total_sent, 130, {
        fontSize: 15,
        fontWeight: 500,
      }),
      getField("opener", lang.statistics.contacts_type.opener),
      getField("clicker", lang.statistics.contacts_type.clicker),
      getField("answerer", lang.statistics.contacts_type.answerer),
      getFieldQuality(
        "answer_positive",
        lang.statistics.contacts_type.postitive
      ),
      getFieldQuality(
        "answer_negative",
        lang.statistics.contacts_type.negative
      ),
      getFieldQuality("answer_neutral", lang.statistics.contacts_type.neutral),
    ],
    [getField, getFieldQuality, lang]
  );

  return (
    <Flex h={500} w={"100%"} pos={"relative"}>
      <LoaderStats />
      <MainTable
        apiRef={apiRef}
        columns={columns}
        rows={rows}
        id={"list-table"}
        withBorderTop
        disableCheckboxes
        tableSx={{
          ".table-toolbar": {
            display: "none",
          },
          ".MuiDataGrid-columnHeader[data-field='id']": {
            pl: 2.5,
          },
        }}
      />
    </Flex>
  );
};

export default ReportingSendersAll;
